<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />

    <div v-if="updateAvailable" class="app-update-banner" role="status">
      <div class="app-update-banner__text">
        New update available
      </div>
      <button class="btn btn-sm btn-primary app-update-banner__btn" type="button" @click="refreshApp">
        Refresh Now
      </button>
    </div>
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch, ref, onMounted, onBeforeUnmount } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout
    const updateAvailable = ref(false)
    const latestSignature = ref(null)
    const checkIntervalMs = 10 * 60 * 1000
    const signatureKey = 'mbuild_signature'
    let updateTimer = null
    let visibilityHandler = null

    const getSignature = async () => {
      if (navigator && navigator.onLine === false) return null

      const url = `${window.location.origin}${window.location.pathname}`
      const tryFetch = async (method) => fetch(url, { method, cache: 'no-store' })

      let res = null
      try {
        res = await tryFetch('HEAD')
        if (!res.ok) res = await tryFetch('GET')
      } catch (error) {
        return null
      }

      const etag = res.headers.get('etag')
      const lastModified = res.headers.get('last-modified')
      return etag || lastModified
    }

    const checkForUpdate = async () => {
      const signature = await getSignature()
      if (!signature) return

      const stored = localStorage.getItem(signatureKey)
      if (!stored) {
        localStorage.setItem(signatureKey, signature)
        return
      }

      if (stored !== signature) {
        latestSignature.value = signature
        updateAvailable.value = true
      }
    }

    const refreshApp = () => {
      if (latestSignature.value) {
        localStorage.setItem(signatureKey, latestSignature.value)
      }
      updateAvailable.value = false
      window.location.reload()
    }

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    onMounted(() => {
      checkForUpdate()
      updateTimer = window.setInterval(checkForUpdate, checkIntervalMs)
      visibilityHandler = () => {
        if (!document.hidden) checkForUpdate()
      }
      window.addEventListener('focus', checkForUpdate)
      document.addEventListener('visibilitychange', visibilityHandler)
    })

    onBeforeUnmount(() => {
      if (updateTimer) window.clearInterval(updateTimer)
      window.removeEventListener('focus', checkForUpdate)
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler)
      }
    })

    return {
      skinClasses,
      enableScrollToTop,
      updateAvailable,
      refreshApp,
    }
  },
}
</script>

<style lang="scss">
.app-update-banner {
  position: fixed;
  right: 24px;
  bottom: 84px;
  z-index: 1050;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.85rem;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  border: 1px solid rgba(79, 70, 229, 0.2);
}

.app-update-banner__text {
  font-size: 0.85rem;
  color: #1f2937;
  font-weight: 600;
  white-space: nowrap;
}

.app-update-banner__btn {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
}
</style>
